import { useState, useEffect } from 'react';
import { 
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogContent,
  DialogClose
} from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";
import { X, Filter, Check } from "lucide-react";
import { router, usePage } from "@inertiajs/react";
import { useFilters } from "../hooks/useFilters";

interface FilterState {
  body_zones: string[];
  specialties: string[];
  practice_locations: string[];
  patient_types: string[];
  payment_methods: string[];
  techniques: string[];
  appointment_methods: string[];
  accessibility: string[];
  equipment: string[];
  languages: string[];
  sports: string[];
  new_patients_policy: string;
  waiting_list: boolean | null;
  practice_mode: string;
  parking_type: string;
  specialist: boolean | null;
}

const AdvancedFilterModal = () => {
  const { loading, getFilterValuesByCategory } = useFilters();
  const page = usePage<any>();
  const currentFilters = page.props.filters || {};
  
  // État centralisé pour tous les filtres - initialisé avec les filtres actuels
  // Convertir waiting_list et specialist de string à booléen si nécessaire
  const getBooleanValue = (value: any): boolean | null => {
    if (value === null || value === undefined || value === "") return null;
    if (typeof value === 'boolean') return value;
    if (typeof value === 'string') {
      if (value === "true") return true;
      if (value === "false") return false;
    }
    return null;
  };

  const [filters, setFilters] = useState<FilterState>({
    body_zones: currentFilters.body_zones || [],
    specialties: currentFilters.specialties || [],
    practice_locations: currentFilters.practice_locations || [],
    patient_types: currentFilters.patient_types || [],
    payment_methods: currentFilters.payment_methods || [],
    techniques: currentFilters.techniques || [],
    appointment_methods: currentFilters.appointment_methods || [],
    accessibility: currentFilters.accessibility || [],
    equipment: currentFilters.equipment || [],
    languages: currentFilters.languages || [],
    sports: currentFilters.sports || [],
    new_patients_policy: currentFilters.new_patients_policy || "",
    waiting_list: getBooleanValue(currentFilters.waiting_list),
    practice_mode: currentFilters.practice_mode || "",
    parking_type: currentFilters.parking_type || "",
    specialist: getBooleanValue(currentFilters.specialist)
  });

  // Mettre à jour les filtres quand les filtres de la page changent
  useEffect(() => {
    if (currentFilters) {
      setFilters({
        body_zones: currentFilters.body_zones || [],
        specialties: currentFilters.specialties || [],
        practice_locations: currentFilters.practice_locations || [],
        patient_types: currentFilters.patient_types || [],
        payment_methods: currentFilters.payment_methods || [],
        techniques: currentFilters.techniques || [],
        appointment_methods: currentFilters.appointment_methods || [],
        accessibility: currentFilters.accessibility || [],
        equipment: currentFilters.equipment || [],
        languages: currentFilters.languages || [],
        sports: currentFilters.sports || [],
        new_patients_policy: currentFilters.new_patients_policy || "",
        waiting_list: getBooleanValue(currentFilters.waiting_list),
        practice_mode: currentFilters.practice_mode || "",
        parking_type: currentFilters.parking_type || "",
        specialist: getBooleanValue(currentFilters.specialist)
      });
    }
  }, [currentFilters]);

  // Fonction pour mettre à jour un filtre
  const updateFilter = (key: keyof FilterState, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Fonction pour gérer les cases à cocher
  const handleCheckboxChange = (filterKey: keyof FilterState, itemId: string, checked: boolean) => {
    const currentArray = filters[filterKey] as string[];
    if (checked) {
      updateFilter(filterKey, [...currentArray, itemId]);
    } else {
      updateFilter(filterKey, currentArray.filter(item => item !== itemId));
    }
  };

  // Fonction pour réinitialiser tous les filtres
  const resetFilters = () => {
    setFilters({
      body_zones: [],
      specialties: [],
      practice_locations: [],
      patient_types: [],
      payment_methods: [],
      techniques: [],
      appointment_methods: [],
      accessibility: [],
      equipment: [],
      languages: [],
      sports: [],
      new_patients_policy: "",
      waiting_list: null,
      practice_mode: "",
      parking_type: "",
      specialist: null
    });
  };

  // Fonction pour appliquer les filtres
  const applyFilters = () => {
    // Construire les paramètres de recherche
    const searchParams: any = {};

    // Préserver la ville/code postal et le nom depuis les filtres actuels de la page
    if (currentFilters.city) searchParams.city = currentFilters.city;
    if (currentFilters.name) searchParams.name = currentFilters.name;

    Object.entries(filters).forEach(([key, value]) => {
      if (Array.isArray(value) && value.length > 0) {
        searchParams[key] = value;
      } else if (typeof value === 'string' && value !== '') {
        searchParams[key] = value;
      } else if (typeof value === 'boolean' && value !== null) {
        // Convertir les booléens en strings pour waiting_list et specialist
        if (key === 'waiting_list' || key === 'specialist') {
          searchParams[key] = value.toString();
        } else {
          searchParams[key] = value;
        }
      }
    });

    // Naviguer vers la page de recherche avec les filtres
    router.get('/search', searchParams, {
      preserveState: true,
      preserveScroll: true,
      only: ['physios', 'filters', 'searchMetadata']
    });
  };

  // Fonction pour obtenir le label d'un filtre booléen
  const getBooleanFilterLabel = (key: string, value: boolean): string => {
    if (key === 'waiting_list') {
      return value ? 'Avec liste d\'attente' : 'Sans liste d\'attente';
    }
    if (key === 'specialist') {
      return 'Spécialiste approuvé';
    }
    return value ? 'Oui' : 'Non';
  };

  // Fonction pour compter les filtres actifs
  const getActiveFiltersCount = () => {
    let count = 0;
    Object.entries(filters).forEach(([, value]) => {
      if (Array.isArray(value) && value.length > 0) {
        count += value.length;
      } else if (typeof value === 'string' && value !== '') {
        count += 1;
      } else if (typeof value === 'boolean' && value !== null) {
        count += 1;
      }
    });
    return count;
  };

  // Fonction pour rendre un groupe de cases à cocher
  const renderCheckboxGroup = (title: string, items: string[], filterKey: keyof FilterState) => {
    const currentValues = filters[filterKey] as string[];
    const selectedCount = currentValues.length;
    
    return (
      <div className="bg-white rounded-lg p-4 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
        <div className="flex items-center justify-between mb-3">
          <label className="text-sm font-semibold text-[#015963]">{title}</label>
          {selectedCount > 0 && (
            <Badge variant="secondary" className="bg-[#008788] text-white text-xs">
              {selectedCount}
            </Badge>
          )}
        </div>
        <div className="space-y-1 max-h-40 overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
          {items.map(item => {
            const isSelected = currentValues.includes(item);
            return (
              <div 
                key={item} 
                className={`flex items-center space-x-3 p-2 rounded-md transition-all ${
                  isSelected 
                    ? 'bg-[#008788]/10 border border-[#008788]/20' 
                    : 'hover:bg-gray-50 border border-transparent'
                }`}
              >
                <Checkbox
                  id={`${filterKey}-${item}`}
                  checked={isSelected}
                  onCheckedChange={(checked) => handleCheckboxChange(filterKey, item, checked === true)}
                  className="text-[#008788] border-[#008788] data-[state=checked]:bg-[#008788]"
                />
                <label 
                  htmlFor={`${filterKey}-${item}`} 
                  className={`text-sm cursor-pointer flex-1 transition-colors ${
                    isSelected ? 'text-[#015963] font-medium' : 'text-gray-700'
                  }`}
                >
                  {item}
                </label>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <DialogContent 
      className="max-w-4xl max-h-[90vh] bg-white p-0 flex flex-col"
      onInteractOutside={() => {
        applyFilters();
      }}
    >
      <DialogHeader className="border-b border-gray-100 pb-4 px-6 pt-6 flex-shrink-0 bg-white">
        <div className="flex items-center justify-between">
          <DialogTitle className="flex items-center gap-3 text-[#015963]">
            <div className="p-2 bg-[#008788]/10 rounded-lg">
              <Filter className="h-5 w-5 text-[#008788]" />
            </div>
            <div>
              <span className="text-xl font-semibold">Filtres avancés</span>
              {getActiveFiltersCount() > 0 && (
                <Badge className="ml-3 bg-[#31953a] text-white">
                  {getActiveFiltersCount()} filtre{getActiveFiltersCount() > 1 ? 's' : ''}
                </Badge>
              )}
            </div>
          </DialogTitle>
          <div className="hidden md:flex gap-2">
            {getActiveFiltersCount() > 0 && (
              <Button 
                variant="ghost"
                size="sm"
                onClick={resetFilters}
                className="text-gray-600 hover:text-red-600"
              >
                <X className="h-4 w-4 mr-1" />
                Réinitialiser
              </Button>
            )}
            <DialogClose asChild>
              <Button 
                onClick={() => {
                  applyFilters();
                }}
                className="bg-[#008788] hover:bg-[#00bcbe] text-white shadow-sm"
              >
                <Check className="h-4 w-4 mr-2" />
                {getActiveFiltersCount() > 0 
                  ? `Voir résultats (${getActiveFiltersCount()})` 
                  : 'Voir tous'}
              </Button>
            </DialogClose>
          </div>
        </div>
      </DialogHeader>

      <div className="space-y-6 px-6 py-6 overflow-y-auto flex-1">
        {/* Filtres actifs */}
        {getActiveFiltersCount() > 0 && (
          <div className="p-4 bg-gradient-to-r from-[#008788]/5 to-[#31953a]/5 rounded-xl border border-[#00bcbe]/20">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-[#015963] flex items-center gap-2">
                <div className="w-2 h-2 bg-[#31953a] rounded-full animate-pulse"></div>
                Filtres actifs ({getActiveFiltersCount()})
              </h3>
              <Button
                variant="outline"
                size="sm"
                onClick={resetFilters}
                className="text-red-600 border-red-300 hover:bg-red-50 hover:border-red-400"
              >
                <X className="h-3 w-3 mr-1" />
                Tout effacer
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {Object.entries(filters).flatMap(([key, value]) => {
                if (Array.isArray(value) && value.length > 0) {
                  return value.map(v => (
                    <Badge key={key + v} className="bg-[#008788] text-white hover:bg-[#00bcbe] transition-colors">
                      {v}
                      <button
                        onClick={() => updateFilter(key as keyof FilterState, value.filter((item) => item !== v))}
                        className="ml-1 hover:bg-white/20 rounded-full p-0.5 transition-colors"
                        type="button"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ));
                } else if (typeof value === 'string' && value !== '') {
                  return [
                    <Badge key={key + value} className="bg-[#008788] text-white hover:bg-[#00bcbe] transition-colors">
                      {value}
                      <button
                        onClick={() => updateFilter(key as keyof FilterState, "")}
                        className="ml-1 hover:bg-white/20 rounded-full p-0.5 transition-colors"
                        type="button"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ];
                } else if (typeof value === 'boolean' && value !== null) {
                  return [
                    <Badge key={key + value} className="bg-[#008788] text-white hover:bg-[#00bcbe] transition-colors">
                      {getBooleanFilterLabel(key, value)}
                      <button
                        onClick={() => updateFilter(key as keyof FilterState, null)}
                        className="ml-1 hover:bg-white/20 rounded-full p-0.5 transition-colors"
                        type="button"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ];
                }
                return [];
              })}
            </div>
          </div>
        )}

        {/* Tous les filtres au même niveau */}
        <div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {loading ? (
              <div className="col-span-2 text-center py-8 text-gray-500">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#008788] mx-auto mb-2"></div>
                Chargement des filtres...
              </div>
            ) : (
              <>
                {/* Filtre spécialiste */}
                <div>
                  <div className="flex items-center space-x-3 p-3 rounded-lg bg-white border border-gray-200 hover:border-[#008788]/30 transition-colors h-full">
                    <Checkbox
                      id="specialist-filter"
                      checked={filters.specialist === true}
                      onCheckedChange={(checked) => updateFilter('specialist', checked === true ? true : null)}
                      className="text-[#008788] border-[#008788] data-[state=checked]:bg-[#008788]"
                    />
                    <label 
                      htmlFor="specialist-filter" 
                      className="text-sm font-medium text-[#015963] cursor-pointer flex-1"
                    >
                      Spécialiste approuvé uniquement
                    </label>
                  </div>
                </div>

                {/* Filtres avec selects */}
                <div>
                  <label className="text-sm font-medium text-[#015963]">Nouveaux patients</label>
                  <select
                    value={filters.new_patients_policy}
                    onChange={(e) => updateFilter('new_patients_policy', e.target.value)}
                    className="mt-1 block w-full rounded-lg border-gray-200 shadow-sm focus:border-[#008788] focus:ring-[#008788] bg-white"
                  >
                    <option value="">Tous</option>
                    {getFilterValuesByCategory('NEW_PATIENTS_POLICIES').map(policy => (
                      <option key={policy} value={policy}>{policy}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-[#015963]">Liste d'attente</label>
                  <select
                    value={filters.waiting_list === null ? "" : filters.waiting_list.toString()}
                    onChange={(e) => updateFilter('waiting_list', e.target.value === "" ? null : e.target.value === "true")}
                    className="mt-1 block w-full rounded-lg border-gray-200 shadow-sm focus:border-[#008788] focus:ring-[#008788] bg-white"
                  >
                    <option value="">Tous</option>
                    <option value="true">Avec liste d'attente</option>
                    <option value="false">Sans liste d'attente</option>
                  </select>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-[#015963]">Mode d'exercice</label>
                  <select
                    value={filters.practice_mode}
                    onChange={(e) => updateFilter('practice_mode', e.target.value)}
                    className="mt-1 block w-full rounded-lg border-gray-200 shadow-sm focus:border-[#008788] focus:ring-[#008788] bg-white"
                  >
                    <option value="">Tous</option>
                    {getFilterValuesByCategory('PRACTICE_MODES').map(mode => (
                      <option key={mode} value={mode}>{mode}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-[#015963]">Stationnement</label>
                  <select
                    value={filters.parking_type}
                    onChange={(e) => updateFilter('parking_type', e.target.value)}
                    className="mt-1 block w-full rounded-lg border-gray-200 shadow-sm focus:border-[#008788] focus:ring-[#008788] bg-white"
                  >
                    <option value="">Tous</option>
                    {getFilterValuesByCategory('PARKING_TYPES').map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>

                {/* Groupes de filtres avec checkboxes */}
                {renderCheckboxGroup("Spécialités", getFilterValuesByCategory('SPECIALTIES'), 'specialties')}
                {renderCheckboxGroup("Techniques", getFilterValuesByCategory('TECHNIQUES'), 'techniques')}
                {renderCheckboxGroup("Équipements", getFilterValuesByCategory('EQUIPMENT'), 'equipment')}
                {renderCheckboxGroup("Lieu d'exercice", getFilterValuesByCategory('PRACTICE_LOCATIONS'), 'practice_locations')}
                {renderCheckboxGroup("Type de patientèle", getFilterValuesByCategory('PATIENT_TYPES'), 'patient_types')}
                {renderCheckboxGroup("Modes de paiement", getFilterValuesByCategory('PAYMENT_METHODS'), 'payment_methods')}
                {renderCheckboxGroup("Prise de rendez-vous", getFilterValuesByCategory('APPOINTMENT_METHODS'), 'appointment_methods')}
                {renderCheckboxGroup("Accessibilité", getFilterValuesByCategory('ACCESSIBILITY'), 'accessibility')}
                {renderCheckboxGroup("Langues parlées", getFilterValuesByCategory('LANGUAGES'), 'languages')}
                {renderCheckboxGroup("Sports", getFilterValuesByCategory('SPORTS'), 'sports')}
              </>
            )}
          </div>
        </div>

      </div>

      <DialogFooter className="border-t border-gray-100 pt-4 px-6 pb-6 flex-shrink-0 bg-white">
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center gap-2">
            {getActiveFiltersCount() > 0 && (
              <Button 
                variant="ghost"
                size="sm"
                onClick={resetFilters}
                className="text-gray-600 hover:text-red-600"
              >
                <X className="h-4 w-4 mr-1" />
                Réinitialiser
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <DialogClose asChild>
              <Button variant="outline" className="text-[#015963] border-[#015963]/30 hover:bg-[#015963]/10">
                Annuler
              </Button>
            </DialogClose>
            <DialogClose asChild>
              <Button 
                onClick={() => {
                  applyFilters();
                }}
                className="bg-[#008788] hover:bg-[#00bcbe] text-white shadow-sm"
              >
                <Check className="h-4 w-4 mr-2" />
                {getActiveFiltersCount() > 0 
                  ? `Voir les résultats (${getActiveFiltersCount()})` 
                  : 'Voir tous les kinés'}
              </Button>
            </DialogClose>
          </div>
        </div>
      </DialogFooter>
    </DialogContent>
  );
};

export default AdvancedFilterModal;
